// Lightweight report catalog metadata — no React/MUI imports so it can be
// pulled into the breadcrumb layer and routing cheaply.
//
// Shared (#1719): the department app, the hub and the api all read this. The
// api validates the `reportId` on a hub request against it, and the hub builds
// its catalog from it, so a report can't exist on one side of the wire and not
// the other.

// `requires` lists the data permissions a report's underlying collections need.
// Holding `reports.read` lets a user into the Reports area, but each report is
// additionally gated on the permission(s) for the data it surfaces — e.g. every
// incident-derived report needs `incidents.read`, so a user with reports.read but
// incidents.read=false never sees them (catalog tile and direct route alike).
//
// These are DEPARTMENT permissions. A hub user holds none of them (there is no
// per-department role in the hub — see #207), so the hub gates on `hubStatus`
// below instead and never consults `requires`.
export const REPORT_CATALOG = [
    // `hubMulti` — this report can be run over SEVERAL departments at once, as
    // a district roll-up. See the note on incidentSummaryRollup in ./rollup.mjs
    // for why that is exact here and is NOT a property to hand out freely:
    // call counts add up, response-time percentiles and attendance rates don't.
    {id: 'incident-summary', group: 'ops', title: 'Incident Summary', icon: 'local_fire_department', question: 'What did we respond to, and how is call volume trending?', status: 'available', hubStatus: 'available', hubMulti: true, requires: ['incidents.read']},
    {id: 'apparatus', group: 'ops', title: 'Apparatus Utilization', icon: 'local_shipping', question: 'How is each apparatus used, and who crews it?', status: 'available', hubStatus: 'available', requires: ['incidents.read']},
    {id: 'member', group: 'ops', title: 'Member Activity', icon: 'groups', question: 'Who\'s responding — including overnight — and who runs incidents?', status: 'available', hubStatus: 'available', requires: ['incidents.read']},
    // #1515 — one printable page per member for performance reviews. `needs`
    // tells the Reports shell which extra collections to load beyond incidents;
    // `noCompare` skips the previous-period incident query (this report has no
    // year-over-year delta chips, so loading it would be pure waste).
    // `users.write` is in `requires` because both the per-member statusChanges
    // rows that drive proration AND the full `users` docs (the roster context
    // carries `startDate`, which usersLight does not) are gated to
    // self-or-users.write in firestore.rules.
    //
    // `deployments` is a SOFT need (#1567) — deliberately absent from
    // `requires`. The feature is per-tenant and carries its own read
    // permission, so demanding it would lock the whole report for a department
    // that doesn't run deployments. The shell loads it only when the feature
    // and permission are both there, and the report reads the same as it did
    // before when they aren't.
    {id: 'member-performance', group: 'ops', title: 'Member Performance Review', icon: 'workspace_premium', question: 'Per member: incidents, training and admin hours against what was possible while active.', status: 'available', needs: ['members', 'trainings', 'adminHours', 'statusChanges', 'deployments'], noCompare: true, hubStatus: 'available', hubAdminOnly: true, requires: ['incidents.read', 'training.read', 'payroll.read', 'users.write']},
    {id: 'response-performance', group: 'gov', title: 'Response Performance', icon: 'timer', question: 'How fast do we respond at the 90th percentile, against the NFPA benchmark?', status: 'available', hubStatus: 'available', featured: true, partial: true, requires: ['incidents.read']},
    // #1668 — the first report that isn't built on incidents at all.
    //
    // `noIncidents` skips the shell's incident query (this report never reads
    // one, and a tenant-wide date-ranged read for numbers nothing renders is
    // pure cost); `noRange` hides the shared past-window toggle, because a
    // forecast looks forward and every preset in `rangeFor` looks back — the
    // report brings its own horizon control instead, and `periodLabel` renames
    // the header field it fills. `gearTree` in `needs` tells the shell to mount
    // GearCategoriesProvider around the report (the provider subscribes itself;
    // there is nothing for the `needs` loader to fetch).
    {id: 'gear-expiry', group: 'gov', title: 'Gear Expiry Forecast', icon: 'event_repeat', question: 'What gear expires when, and what will it cost to replace?', status: 'available', needs: ['gearTree'], noCompare: true, noIncidents: true, noRange: true, periodLabel: 'Forecast window', requires: ['gear.read']},
    // The board packet as ONE printable document: what we responded to,
    // how fast, who turned out, and what we trained on. It replaces the deck a
    // chief otherwise builds by hand every quarter, which is why the period
    // control grows quarter presets (see `rangeFor` in ./range.mjs) rather than
    // the report hard-coding a quarter.
    //
    // The second report to declare `needs: ['trainings']`, and the only one
    // outside the performance review that draws on two pools at once — hence
    // `training.read` beside `incidents.read`.
    //
    // `serviceHistory` is a SOFT need and is deliberately absent from
    // `requires`. Promotions and new recruits live behind `users.write`
    // (users/{id}.startDate and users/{id}/rankChanges are both PII-gated), and
    // demanding it would put an officer-level gate on a document meant for a
    // council. The shell asks for it only when the reader already holds that
    // permission, and the report drops those two tiles when it doesn't — same
    // posture as `deployments` on the performance review (#1567) — which is
    // also where `deployments` itself comes from: a per-tenant feature with its
    // own read permission, so the section is absent rather than empty when the
    // department doesn't run them or the reader can't see them.
    //
    // No `hubStatus`: the api's /hub/reportsData projects no training data, so
    // the hub could only ever render half of this report.
    {id: 'operations', group: 'gov', title: 'Operations Report', icon: 'stacked_bar_chart', question: 'The board packet: calls, response times, who turned out and what we trained on — for any quarter or year.', status: 'available', needs: ['trainings', 'serviceHistory', 'deployments'], requires: ['incidents.read', 'training.read']},
    // #2305 — who is reaching 5, 10, 15, 20 … years of service, and whether each
    // milestone has been awarded, so a district can order medals ahead of time.
    // The second report not built on incidents.
    //
    // `noIncidents`/`noRange`/`noCompare` for the same reasons as the gear
    // forecast: it reads no runs, and it looks FORWARD, so it brings its own
    // "coming up within" control. It loads its own data from the api (the
    // awards are HR records, which no browser reads directly), hence no `needs`.
    //
    // `users.write` because service dates live on the full `users` docs, and
    // `hr.read` because the awards are HR records. The report is read-only:
    // awards are recorded on the member's Records tab. Officer-level, so
    // `hubAdminOnly` — which today is every hub user (a hub user is an admin of
    // each department it covers).
    //
    // `hubMulti`: a medal count adds up across departments — that is the whole
    // point of a district ordering medals once — so the hub runs it over every
    // visible department at once.
    {id: 'service-awards', group: 'gov', title: 'Service Awards', icon: 'military_tech', question: 'Who is reaching 5, 10, 15 or 20 years of service, and which have been awarded?', status: 'available', noCompare: true, noIncidents: true, noRange: true, periodLabel: 'Award window', hubStatus: 'available', hubMulti: true, hubAdminOnly: true, requires: ['users.write', 'hr.read']},
    // #2303 — where each hall stands on its competencies, and what it is
    // missing. Built on the Training Matrix's cells rather than on incidents, so
    // `noIncidents` skips the shell's incident query and `competency` in `needs`
    // has the department app read training the way its matrix does (evidence
    // mode) with the roster. The hub doesn't use /hub/reportsData for it: it
    // reuses /hub/trainingMatrix, which already sends results and never a record.
    //
    // `hubMulti`: its figures are counts of members, which add up across halls,
    // and comparing halls is the point. `hubAdminOnly` and `training.write`
    // match the Training Matrix, which the report names members from.
    {id: 'competency-progress', group: 'ops', title: 'Competency Progress', icon: 'school', question: 'How far along is each department on its competencies, and what is it missing?', status: 'available', needs: ['competency'], noCompare: true, noIncidents: true, hubStatus: 'available', hubMulti: true, hubAdminOnly: true, requires: ['training.write']},
    {id: 'ofc', group: 'reg', title: 'OFC Fire Loss Return', icon: 'description', question: 'Formal BC Office of the Fire Commissioner return — dollar loss, cause & origin, casualties.', status: 'soon', requires: ['incidents.read']},
    {id: 'mutual-aid', group: 'reg', title: 'Mutual-Aid Report', icon: 'swap_horiz', question: 'Aid given to and received from neighbouring departments.', status: 'soon', requires: ['incidents.read']},
    {id: 'ev', group: 'reg', title: 'Emerging-Hazard (EV / Battery)', icon: 'electric_bolt', question: 'Lithium-ion and EV incident tracking for emerging-risk planning.', status: 'soon', requires: ['incidents.read']}
];

export const reportMeta = id => REPORT_CATALOG.find(c => c.id === id) || {};

// True when the user holds every data permission a report requires. `has` is a
// `permission => boolean` predicate (pass `p => hasPermission(currentUser, p)`),
// which keeps this lightweight module free of the permission/React imports the
// catalog and shell layers pull in.
export const canViewReport = (report, has) => (report.requires || []).every(has);

// ---- hub view (#1719) ----------------------------------------------------
//
// A report opts IN to the hub with `hubStatus: 'available'`, and the line it
// has to clear is the DEPARTMENT permission in its own `requires`.
//
// The hub has two roles, user and admin, and every authenticated hub user reads
// every department in it (routes/hub/_auth.js states this outright; per-user
// department scoping is #207). So there are exactly two gates a hub report can
// sit behind, and `hubAdminOnly` picks the stricter one.
//
// Most reports are gated on `incidents.read` in the department app, which any
// member holds, so they need no hub gate beyond being signed in.
//
// **Member Performance Review is the exception.** It additionally needs
// `users.write`, `payroll.read` and `training.read` — an officer-level gate —
// because it is built on per-member status history, approved timesheets and
// training attendance. `hubAdminOnly` marks it: the hub route, the catalog and
// the api all read this one flag, and admin is the closest the hub has to the
// department's officer permission until #207 lands.
//
// The api narrows it further than the flag does: a `statusChanges` row carries
// a free-text note and an attachment — where a medical reason for a leave is
// written, and why #1516 restricted the subcollection — and the hub is sent
// neither. It learns WHEN a member was inactive, never why.
//
// Gear Expiry stays off the hub for a different reason: `gear.read` is a gate
// the hub could express, but there is no hub gear surface for it to sit on.
//
// `hubReportMeta` re-labels a report for the hub catalog so the tiles, the
// route guard and the api's own allowlist can all read one shape.
export const HUB_REPORT_IDS = REPORT_CATALOG.filter(c => c.hubStatus === 'available').map(c => c.id);

export const isHubReport = id => HUB_REPORT_IDS.includes(id);

// True when a report needs more than a signed-in hub user. Read by the hub's
// route guard and its catalog, and enforced independently by the api.
export const isHubAdminReport = id => reportMeta(id).hubAdminOnly === true;

// True when a report can be run across several departments at once. Drives the
// hub's picker (multi-select vs single) and is what the api checks before
// accepting more than one projectId, so a report can't be rolled up merely by
// sending it a longer list.
export const isHubMultiReport = id => reportMeta(id).hubMulti === true;

export const hubReportMeta = id => {
    const meta = reportMeta(id);
    return meta.id ? {...meta, status: meta.hubStatus || 'soon'} : meta;
};

export const HUB_REPORT_CATALOG = REPORT_CATALOG.map(c => ({...c, status: c.hubStatus || 'soon'}));

// Accent colours (MUI palette tokens) for a report's icon tile — shared between
// the catalog cards and the report's branded header so they always match.
export const reportIconStyle = c => {
    if (!c || c.status === 'soon') {
        return {bg: 'grey.100', color: 'text.disabled'};
    }
    const map = {
        'incident-summary': ['error.light', 'error.main'],
        apparatus: ['info.light', 'info.main'],
        member: ['success.light', 'success.main'],
        'member-performance': ['warning.light', 'warning.main'],
        'response-performance': ['grey.200', 'primary.main'],
        'gear-expiry': ['warning.light', 'warning.main'],
        operations: ['info.light', 'primary.main'],
        'service-awards': ['warning.light', 'warning.dark'],
        'competency-progress': ['success.light', 'success.dark']
    };
    const m = map[c.id] || ['grey.100', 'text.secondary'];
    return {bg: m[0], color: m[1]};
};

export const REPORT_GROUPS = [
    {key: 'ops', title: 'Operations', subtitle: 'Internal situational awareness'},
    {key: 'gov', title: 'Governance & Budget', subtitle: 'Defensible numbers for councils & regional boards'},
    {key: 'reg', title: 'Regulatory — BC OFC', subtitle: 'Formal fire reporting · gated on field capture'}
];
